import { useEffect, useRef, useState } from 'react';
import { type Banner } from '@/domain';
import { cn } from '@/lib/cn';
import { HorizontalScrollRow } from '@/ui/common';
import { BannerSlide } from './BannerSlide';

const AUTOPLAY_MS = 6500;

export interface BannerHeroCarouselProps {
  banners: Banner[];
  size?: 'mobile' | 'desktop';
  className?: string;
}

export function BannerHeroCarousel({ banners, size = 'mobile', className }: BannerHeroCarouselProps) {
  if (banners.length === 0) return null;

  const resetKey = banners.map((banner) => String(banner.id)).join(':');
  return (
    <BannerHeroCarouselView
      key={resetKey}
      banners={banners}
      size={size}
      className={className}
    />
  );
}

function BannerHeroCarouselView({ banners, size = 'mobile', className }: BannerHeroCarouselProps) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const pausedRef = useRef(false);
  const activeIndexRef = useRef(0);
  const isMobile = size === 'mobile';
  const canLoop = banners.length > 1;

  useEffect(() => {
    activeIndexRef.current = activeIndex;
  }, [activeIndex]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const onScroll = () => {
      const first = viewport.querySelector<HTMLElement>('[data-banner-slide]');
      if (!first) return;
      const styles = getComputedStyle(viewport);
      const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
      const stride = first.getBoundingClientRect().width + gap;
      if (stride <= 0) return;
      const next = Math.round(viewport.scrollLeft / stride);
      setActiveIndex(Math.min(Math.max(next, 0), banners.length - 1));
    };

    viewport.addEventListener('scroll', onScroll, { passive: true });
    return () => viewport.removeEventListener('scroll', onScroll);
  }, [banners.length]);

  function scrollToSlide(index: number) {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const slide = viewport.querySelectorAll<HTMLElement>('[data-banner-slide]')[index];
    if (!slide) return;
    // Scroll only the carousel viewport — scrollIntoView also moves the page.
    viewport.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
  }

  useEffect(() => {
    if (!canLoop) return;

    const id = window.setInterval(() => {
      if (pausedRef.current) return;
      const viewport = viewportRef.current;
      if (!viewport) return;
      const slideCount = viewport.querySelectorAll('[data-banner-slide]').length;
      if (slideCount === 0) return;
      const next = (activeIndexRef.current + 1) % slideCount;
      scrollToSlide(next);
    }, AUTOPLAY_MS);

    return () => window.clearInterval(id);
  }, [canLoop]);

  function pause() {
    pausedRef.current = true;
  }

  function resume() {
    pausedRef.current = false;
  }

  function goTo(index: number) {
    scrollToSlide(index);
  }

  return (
    <div
      className={cn('flex w-full flex-col items-center gap-2', className)}
      onPointerEnter={pause}
      onPointerLeave={resume}
      onFocusCapture={pause}
      onBlurCapture={resume}
    >
      <HorizontalScrollRow
        scrollerRef={viewportRef}
        withDesktopArrows={canLoop}
        loop={canLoop}
        role="region"
        aria-roledescription="carousel"
        aria-label="Promotional banners"
        className={cn(
          'w-full snap-x snap-mandatory overflow-y-clip pl-0',
          isMobile ? 'h-[200px] gap-2.5' : 'h-[287px] gap-4',
        )}
      >
        {banners.map((banner) => (
          <div
            key={banner.id}
            data-banner-slide
            className={cn(
              'shrink-0 snap-start',
              isMobile ? 'h-[200px] w-[312px]' : 'h-[287px] w-[448px]',
            )}
          >
            <BannerSlide banner={banner} />
          </div>
        ))}
      </HorizontalScrollRow>

      {canLoop ? (
        <div className="flex items-center justify-center gap-[5px]" role="tablist" aria-label="Banner slides">
          {banners.map((banner, index) => {
            const active = index === activeIndex;
            return (
              <button
                key={banner.id}
                type="button"
                role="tab"
                aria-selected={active}
                aria-label={`Slide ${index + 1}`}
                className={cn(
                  'rounded-full',
                  active ? 'h-1 w-2 bg-text-primary' : 'size-1 bg-bg-elevated',
                )}
                onClick={() => goTo(index)}
              />
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
